import { useEffect, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import { PlatformBadgeRow } from "@/components/PlatformBadge";
import AnnouncementBanner from "@/components/AnnouncementBanner";
import { ShareFlipDialog } from "@/components/ShareFlipDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  ArrowLeft, ArrowRight, Vault, SignOut, Archive, CurrencyDollar, ImageSquare, Clock,
  FileCsv, FileJs, PencilSimple, Storefront, CheckCircle, Trophy, TrendUp, ChartLineUp,
  PaperPlaneTilt, Confetti, CaretRight, Trash, Plus, Tag, Crown, ShareNetwork,
} from "@phosphor-icons/react";

const fmtPrice = (v) => (v || v === 0 ? `$${Number(v).toLocaleString()}` : "—");
const fmtMonth = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "long", year: "numeric" });
  } catch (e) {
    return "—";
  }
};

const NEED_LABELS = {
  needs_photos: { text: "Needs photos", color: "#B45309" },
  needs_details: { text: "Needs description & price", color: "#B45309" },
  ready: { text: "Ready to finish & list", color: "#047857" },
};

const PLATFORMS = ["Chairish", "1stDibs", "AptDeco", "Ruby Lane", "Kaiyo", "Pamono", "Etsy", "eBay", "Facebook Marketplace", "Instagram", "Local / In person", "Other"];
// Curated high-end resale marketplaces only — the recommender must never suggest general/peer marketplaces.
const CURATED_PLATFORMS = ["Chairish", "1stDibs", "AptDeco", "Ruby Lane", "Kaiyo", "Pamono"];

// Derived — no manual tagging needed to see membership. A piece's platforms come from its
// platform_listings (what the seller tracks); if none yet, we imply its Chairish source.
function platformEntries(l) {
  const pl = Array.isArray(l.platform_listings) ? l.platform_listings : [];
  if (pl.length) return pl;
  return [{ platform: "Chairish", price: l.price_retail, reserve: l.price_minimum_acceptable, implicit: true }];
}
function platformsForListing(l) {
  const set = new Set(platformEntries(l).map((e) => e.platform));
  if (l.status === "Sold" && l.sold_platform) set.add(l.sold_platform);
  return [...set];
}

function FilterChip({ label, count, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-sm font-semibold px-3 py-1.5 rounded-full border transition-colors ${
        active ? "bg-[#0D1B2A] text-white border-[#0D1B2A]" : "bg-white text-[#525252] border-[#D8D3C8] hover:border-[#0D1B2A] hover:text-[#0D1B2A]"
      }`}
    >
      {label} <span className={active ? "text-white/70" : "text-[#8A867D]"}>{count}</span>
    </button>
  );
}

function PlatformPricingEditor({ listing, onSaved }) {
  const [entries, setEntries] = useState([]);
  const [addPlatform, setAddPlatform] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const pl = Array.isArray(listing.platform_listings) ? listing.platform_listings : [];
    if (pl.length) setEntries(pl.map((e) => ({ platform: e.platform, price: e.price ?? "", reserve: e.reserve ?? "" })));
    else setEntries([{ platform: "Chairish", price: listing.price_retail ?? "", reserve: listing.price_minimum_acceptable ?? "" }]);
  }, [listing]);

  const used = entries.map((e) => e.platform);
  const addable = PLATFORMS.filter((p) => !used.includes(p));
  const update = (i, field, val) => setEntries((prev) => prev.map((e, idx) => (idx === i ? { ...e, [field]: val } : e)));
  const remove = (i) => setEntries((prev) => prev.filter((_, idx) => idx !== i));
  const add = () => {
    if (!addPlatform) return;
    setEntries((prev) => [...prev, { platform: addPlatform, price: listing.price_retail ?? "", reserve: listing.price_minimum_acceptable ?? "" }]);
    setAddPlatform("");
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = entries.filter((e) => e.platform).map((e) => ({
        platform: e.platform,
        price: e.price === "" || e.price == null ? null : parseFloat(e.price),
        reserve: e.reserve === "" || e.reserve == null ? null : parseFloat(e.reserve),
      }));
      const { data } = await api.put(`/listings/${listing.id}/platform-listings`, { platform_listings: payload });
      toast.success("Platform pricing saved — it's yours to keep.");
      onSaved(data);
    } catch (e) {
      toast.error("Couldn't save — please try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="platform-pricing-editor" className="border border-[#D8D3C8] bg-white p-4">
      <p className="label-caps mb-1">Where it's listed &amp; at what price</p>
      <p className="text-sm text-[#8A867D] mb-3 max-w-2xl">
        Track each marketplace this piece is on and its price there — the one view no single platform gives you.
        Reserve = the lowest you'll take on an offer. (Each platform shows your net after fees as you price, so we don't repeat that.)
      </p>
      <div className="space-y-2">
        <div className="grid grid-cols-12 gap-2 label-caps text-[#8A867D]">
          <span className="col-span-5">Marketplace</span>
          <span className="col-span-3">List price</span>
          <span className="col-span-3">Reserve</span>
          <span className="col-span-1" />
        </div>
        {entries.map((e, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`pp-row-${i}`}>
            <span className="col-span-5 text-[15px] font-medium text-[#0D1B2A] truncate">{e.platform}</span>
            <input data-testid={`pp-price-${i}`} type="number" min="0" step="0.01" value={e.price} onChange={(ev) => update(i, "price", ev.target.value)} placeholder="$" className="col-span-3 h-10 border border-[#D8D3C8] rounded-none px-2 text-[15px] focus:border-[#0D1B2A] outline-none" />
            <input data-testid={`pp-reserve-${i}`} type="number" min="0" step="0.01" value={e.reserve} onChange={(ev) => update(i, "reserve", ev.target.value)} placeholder="$" className="col-span-3 h-10 border border-[#D8D3C8] rounded-none px-2 text-[15px] focus:border-[#0D1B2A] outline-none" />
            <button data-testid={`pp-remove-${i}`} onClick={() => remove(i)} title="Remove marketplace" className="col-span-1 h-10 flex items-center justify-center text-[#8A867D] hover:text-[#B47A3C] transition-colors"><Trash size={16} /></button>
          </div>
        ))}
        {entries.length === 0 && <p className="text-sm text-[#8A867D]">Not tracking any marketplace yet — add one below.</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-4">
        {addable.length > 0 && (
          <>
            <select data-testid="pp-add-select" value={addPlatform} onChange={(e) => setAddPlatform(e.target.value)} className="h-10 border border-[#D8D3C8] rounded-none px-2 text-[15px] focus:border-[#0D1B2A] outline-none">
              <option value="">Add a marketplace…</option>
              {addable.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <button data-testid="pp-add-button" onClick={add} className="h-10 px-3 border border-[#0D1B2A] rounded-none text-sm font-medium inline-flex items-center gap-1.5 hover:bg-[#0D1B2A] hover:text-white transition-colors"><Plus size={15} weight="bold" /> Add</button>
          </>
        )}
        <button data-testid="pp-save-button" onClick={save} disabled={busy} className="h-10 px-4 bg-[#0D1B2A] text-white rounded-none text-sm font-medium inline-flex items-center gap-1.5 hover:bg-black transition-transform active:scale-95 disabled:opacity-60 ml-auto">{busy ? "Saving…" : "Save pricing"}</button>
      </div>
    </div>
  );
}

const LIST_ADJUST_OPTIONS = [
  { label: "Same as base price", value: 0 },
  { label: "10% less", value: 10 },
  { label: "15% less", value: 15 },
  { label: "20% less", value: 20 },
];
const RESERVE_OPTIONS = [
  { label: "No reserve (you set it on the platform)", value: "" },
  { label: "10% below list", value: 10 },
  { label: "15% below list", value: 15 },
  { label: "20% below list", value: 20 },
];

function BulkPriceDialog({ open, items, onClose, onApplied }) {
  const [step, setStep] = useState("config");
  const [platform, setPlatform] = useState(PLATFORMS[0]);
  const [listAdjust, setListAdjust] = useState(0);
  const [reservePct, setReservePct] = useState("");
  const [busy, setBusy] = useState(false);
  const count = items.length;

  useEffect(() => {
    if (open) {
      setStep("config");
      setPlatform(PLATFORMS[0]);
      setListAdjust(0);
      setReservePct("");
    }
  }, [open]);

  const preview = items.map((l) => {
    const base = Number(l.price_retail || 0);
    const list = Math.round(base * (1 - Number(listAdjust) / 100) * 100) / 100;
    const reserve = reservePct === "" ? null : Math.round(list * (1 - Number(reservePct) / 100) * 100) / 100;
    return { id: l.id, title: l.listing_headline || "Untitled piece", base, list, reserve, noPrice: !base };
  });
  const pricedCount = preview.filter((p) => !p.noPrice).length;
  const skipped = preview.length - pricedCount;

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/listings/bulk-platform-pricing", {
        listing_ids: items.map((l) => l.id),
        platform,
        list_adjust_pct: Number(listAdjust),
        reserve_pct: reservePct === "" ? null : Number(reservePct),
      });
      toast.success(`Priced ${data.updated} ${data.updated === 1 ? "piece" : "pieces"} for ${platform}.`);
      onApplied();
    } catch (e) {
      toast.error("Couldn't apply bulk pricing — please try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="bulk-price-dialog">
        <DialogHeader>
          <div className="inline-flex items-center gap-2 mb-1"><Tag size={20} weight="fill" /> <span className="label-caps">Cross-list · bulk pricing</span></div>
          <DialogTitle className="font-serif-display text-2xl font-bold tracking-tight">
            {step === "config" ? `Price ${count} ${count === 1 ? "piece" : "pieces"} for a marketplace` : "Review before you apply"}
          </DialogTitle>
          <DialogDescription className="text-[#525252]">
            {step === "config"
              ? "Some platforms sell for a bit less on average. Set a list price (and optional reserve) — calculated from each piece's saved price. You'll review every piece before anything is saved."
              : `Here's exactly what each piece becomes on ${platform}. Nothing is saved until you confirm.`}
          </DialogDescription>
        </DialogHeader>

        {step === "config" ? (
          <div className="space-y-4 mt-1">
            <div>
              <label className="label-caps block mb-1.5">Marketplace</label>
              <select data-testid="bulk-platform-select" value={platform} onChange={(e) => setPlatform(e.target.value)} className="h-12 w-full border border-[#0D1B2A]/30 rounded-none px-2 text-[15px] focus:border-[#0D1B2A] outline-none">
                {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="label-caps block mb-1.5">List price</label>
              <select data-testid="bulk-listadjust-select" value={listAdjust} onChange={(e) => setListAdjust(e.target.value)} className="h-12 w-full border border-[#0D1B2A]/30 rounded-none px-2 text-[15px] focus:border-[#0D1B2A] outline-none">
                {LIST_ADJUST_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label-caps block mb-1.5">Reserve <span className="text-[#8A867D] normal-case font-normal">(lowest you'll accept)</span></label>
              <select data-testid="bulk-reserve-select" value={reservePct} onChange={(e) => setReservePct(e.target.value)} className="h-12 w-full border border-[#0D1B2A]/30 rounded-none px-2 text-[15px] focus:border-[#0D1B2A] outline-none">
                {RESERVE_OPTIONS.map((o) => <option key={o.label} value={o.value}>{o.label}</option>)}
              </select>
              <p className="text-xs text-[#8A867D] mt-1.5">Reserve options are kept modest on purpose — no surprise deep discounts. Leave on "No reserve" to set it yourself on the platform.</p>
            </div>
            <button data-testid="bulk-review-button" onClick={() => setStep("review")} className="w-full h-14 bg-[#0D1B2A] text-[#FDFBF7] rounded-none font-medium text-[17px] inline-flex items-center justify-center gap-2 hover:bg-black transition-transform active:scale-[0.99]">
              Review {count} {count === 1 ? "piece" : "pieces"} <ArrowRight size={18} weight="bold" />
            </button>
          </div>
        ) : (
          <div className="mt-1">
            <div className="border border-[#E5E5E5] rounded-md overflow-hidden max-h-[46vh] overflow-y-auto" data-testid="bulk-review-list">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-[#F5F3ED]">
                  <tr className="text-left">
                    <th className="px-3 py-2 label-caps font-semibold">Piece</th>
                    <th className="px-3 py-2 label-caps font-semibold text-right">Base</th>
                    <th className="px-3 py-2 label-caps font-semibold text-right">List</th>
                    <th className="px-3 py-2 label-caps font-semibold text-right">Reserve</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.map((p, i) => (
                    <tr key={p.id} data-testid={`bulk-review-row-${i}`} className="border-t border-[#F0EFEA]">
                      <td className="px-3 py-2 max-w-[220px] truncate">{p.title}</td>
                      <td className="px-3 py-2 text-right text-[#8A867D]">{p.noPrice ? "—" : fmtPrice(p.base)}</td>
                      <td className="px-3 py-2 text-right font-semibold">{p.noPrice ? <span className="text-[#B45309]">needs base price</span> : fmtPrice(p.list)}</td>
                      <td className="px-3 py-2 text-right">{p.reserve == null ? "—" : fmtPrice(p.reserve)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {skipped > 0 && <p className="text-xs text-[#B45309] mt-2" data-testid="bulk-skipped-note">{skipped} piece{skipped === 1 ? "" : "s"} without a saved price will be skipped — add a price first.</p>}
            <div className="flex items-center gap-2 mt-4">
              <button data-testid="bulk-back-button" onClick={() => setStep("config")} className="h-12 px-4 border border-[#0D1B2A] rounded-none font-medium hover:bg-[#F5F3ED] transition-colors">Back</button>
              <button data-testid="bulk-apply-button" onClick={apply} disabled={busy || pricedCount === 0} className="flex-1 h-12 bg-[#0D1B2A] text-[#FDFBF7] rounded-none font-medium inline-flex items-center justify-center gap-2 hover:bg-black transition-transform active:scale-[0.99] disabled:opacity-60">
                {busy ? "Applying…" : `Confirm & apply to ${pricedCount} ${pricedCount === 1 ? "piece" : "pieces"}`} <CheckCircle size={18} weight="fill" />
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MarkSoldDialog({ listing, sellerName, onClose, onSold }) {
  const open = !!listing;
  const [soldPrice, setSoldPrice] = useState("");
  const [platform, setPlatform] = useState(PLATFORMS[0]);
  const [paid, setPaid] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (listing) {
      setSoldPrice(listing.price_retail != null ? String(listing.price_retail) : "");
      setPlatform(PLATFORMS[0]);
      setPaid("");
      setDate(new Date().toISOString().slice(0, 10));
    }
  }, [listing]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/listings/${listing.id}/mark-sold`, {
        sold_price: parseFloat(soldPrice) || 0,
        sold_platform: platform,
        purchase_price: paid ? parseFloat(paid) : null,
        sold_at: date ? new Date(date).toISOString() : null,
      });
      const found = paid ? parseFloat(paid) : 0;
      const sold = parseFloat(soldPrice) || 0;
      onSold({
        title: data.listing_headline || "My sale",
        source: platform,
        found_price: found,
        sold_price: sold,
        multiple: found > 0 ? Math.round(sold / found) : null,
        seller_name: sellerName,
        image_url: data.image_url || "",
      });
    } catch (err) {
      toast.error("Couldn't mark as sold — please try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="mark-sold-dialog">
        <DialogHeader>
          <div className="inline-flex items-center gap-2 mb-1"><Storefront size={20} weight="fill" /> <span className="label-caps">Mark as sold</span></div>
          <DialogTitle className="font-serif-display text-2xl font-bold tracking-tight">{listing?.listing_headline || "This piece"}</DialogTitle>
          <DialogDescription className="text-[#525252]">Tell us the sale details — we'll log it and hand you a share-ready win card.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-1">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label-caps block mb-1.5">Sold price</label>
              <input data-testid="sold-price-input" type="number" min="0" step="0.01" required value={soldPrice} onChange={(e) => setSoldPrice(e.target.value)} placeholder="$" className="h-12 w-full border border-[#0D1B2A]/30 rounded-none px-3 text-[16px] focus:border-[#0D1B2A] outline-none" />
            </div>
            <div>
              <label className="label-caps block mb-1.5">What you paid <span className="text-[#8A867D] normal-case font-normal">(optional)</span></label>
              <input data-testid="sold-paid-input" type="number" min="0" step="0.01" value={paid} onChange={(e) => setPaid(e.target.value)} placeholder="$" className="h-12 w-full border border-[#0D1B2A]/30 rounded-none px-3 text-[16px] focus:border-[#0D1B2A] outline-none" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label-caps block mb-1.5">Sold on</label>
              <select data-testid="sold-platform-select" value={platform} onChange={(e) => setPlatform(e.target.value)} className="h-12 w-full border border-[#0D1B2A]/30 rounded-none px-2 text-[15px] focus:border-[#0D1B2A] outline-none">
                {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="label-caps block mb-1.5">Date sold</label>
              <input data-testid="sold-date-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-12 w-full border border-[#0D1B2A]/30 rounded-none px-3 text-[15px] focus:border-[#0D1B2A] outline-none" />
            </div>
          </div>
          <button data-testid="mark-sold-submit" type="submit" disabled={busy} className="w-full h-14 bg-[#0D1B2A] text-[#FDFBF7] rounded-none font-medium text-[17px] inline-flex items-center justify-center gap-2 hover:bg-black transition-transform active:scale-[0.99] disabled:opacity-60">
            {busy ? "Saving…" : "Mark as sold & share"} <ArrowRight size={18} weight="bold" />
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RerouteDialog({ listing, onClose, onRerouted }) {
  const open = !!listing;
  const [rejectedFrom, setRejectedFrom] = useState("");
  const [nextPlatform, setNextPlatform] = useState("");
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [basis, setBasis] = useState("overall");

  useEffect(() => {
    if (listing) {
      setRejectedFrom("");
      setNextPlatform("");
    }
  }, [listing]);

  useEffect(() => {
    if (!listing) return;
    const excl = [rejectedFrom, ...(listing.rejected_from || [])].filter(Boolean);
    const cat = listing.item_category || "";
    api.get(`/placement-suggestion?exclude=${encodeURIComponent(excl.join(","))}&category=${encodeURIComponent(cat)}`)
      .then(({ data }) => { setSuggestions(data.suggestions || []); setBasis(data.basis || "overall"); })
      .catch(() => { setSuggestions([]); setBasis("overall"); });
  }, [listing, rejectedFrom]);

  const nextOptions = PLATFORMS.filter((p) => p !== rejectedFrom);

  const excludeSet = new Set([rejectedFrom, ...((listing && listing.rejected_from) || [])].filter(Boolean));
  const suggested = suggestions.find(
    (s) => s.enough_signal && s.acceptance_rate != null && CURATED_PLATFORMS.includes(s.platform) && !excludeSet.has(s.platform)
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!nextPlatform) {
      toast.error("Pick where to send it next");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/listings/${listing.id}/reroute`, {
        next_platform: nextPlatform,
        rejected_from: rejectedFrom || "",
      });
      onRerouted(data, nextPlatform);
    } catch (err) {
      toast.error("Couldn't save that — please try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="reroute-dialog">
        <DialogHeader>
          <div className="inline-flex items-center gap-2 mb-1"><Confetti size={20} weight="fill" className="text-[#B47A3C]" /> <span className="label-caps">Their loss!</span></div>
          <DialogTitle className="font-serif-display text-2xl font-bold tracking-tight">Where to next?</DialogTitle>
          <DialogDescription className="text-[#525252]">
            One marketplace passing doesn't define your piece. Point it at its next best home — your
            record is ready to go, so there's nothing to redo.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-1">
          <div>
            <label className="label-caps block mb-1.5">Turned down by <span className="text-[#8A867D] normal-case font-normal">(optional)</span></label>
            <select data-testid="reroute-rejected-select" value={rejectedFrom} onChange={(e) => setRejectedFrom(e.target.value)} className="h-12 w-full border border-[#0D1B2A]/30 rounded-none px-2 text-[15px] focus:border-[#0D1B2A] outline-none">
              <option value="">Prefer not to say</option>
              {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          {suggested && (
            <div className="border border-[#B47A3C]/40 bg-[#FBEDE7]/60 p-3 flex items-start gap-2.5" data-testid="reroute-suggestion">
              <ChartLineUp size={18} weight="fill" className="text-[#B47A3C] shrink-0 mt-0.5" />
              <div className="flex-1 text-sm text-[#525252] leading-snug">
                <span>
                  {basis === "category" && listing?.item_category
                    ? <>Sellers get <strong>{listing.item_category.toLowerCase()}</strong> accepted on <strong className="text-[#0D1B2A]">{suggested.platform}</strong> most often — {suggested.acceptance_rate}% across {suggested.submitted} tracked.</>
                    : (listing?.item_category
                        ? <>Not enough <strong>{listing.item_category.toLowerCase()}</strong> signal yet — across all pieces, <strong className="text-[#0D1B2A]">{suggested.platform}</strong> accepts most often ({suggested.acceptance_rate}% of {suggested.submitted} tracked).</>
                        : <>Across all pieces, <strong className="text-[#0D1B2A]">{suggested.platform}</strong> accepts most often — {suggested.acceptance_rate}% across {suggested.submitted} tracked.</>)}
                </span>
                {nextPlatform !== suggested.platform && (
                  <button type="button" onClick={() => setNextPlatform(suggested.platform)} data-testid="reroute-use-suggestion" className="block mt-1 font-semibold text-[#B47A3C] underline underline-offset-2 hover:no-underline">
                    Send it to {suggested.platform}
                  </button>
                )}
              </div>
            </div>
          )}
          <div>
            <label className="label-caps block mb-1.5">Send it to</label>
            <select data-testid="reroute-next-select" required value={nextPlatform} onChange={(e) => setNextPlatform(e.target.value)} className="h-12 w-full border border-[#0D1B2A]/30 rounded-none px-2 text-[15px] focus:border-[#0D1B2A] outline-none">
              <option value="" disabled>Choose a marketplace…</option>
              {nextOptions.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <button data-testid="reroute-submit" type="submit" disabled={busy} className="w-full h-14 bg-[#0D1B2A] text-[#FDFBF7] rounded-none font-medium text-[17px] inline-flex items-center justify-center gap-2 hover:bg-black transition-transform active:scale-[0.99] disabled:opacity-60">
            {busy ? "Saving…" : "Send it there"} <PaperPlaneTilt size={18} weight="fill" />
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ExportPackDialog({ open, count, ids, onClose }) {
  const [platform, setPlatform] = useState(PLATFORMS[0]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setPlatform(PLATFORMS[0]); }, [open]);

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.post("/export/cross-list-pack.zip", { listing_ids: ids, platform, base_url: process.env.REACT_APP_BACKEND_URL }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      const safe = platform.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
      a.download = `crosslist_pack_${safe}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Cross-list pack for ${platform} downloaded (${count} ${count === 1 ? "piece" : "pieces"} + photos).`);
      onClose();
    } catch (e) {
      toast.error("Couldn't build the pack — please try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="export-pack-dialog">
        <DialogHeader>
          <div className="inline-flex items-center gap-2 mb-1"><Archive size={20} weight="fill" /> <span className="label-caps">Cross-list pack</span></div>
          <DialogTitle className="font-serif-display text-2xl font-bold tracking-tight">Export {count} {count === 1 ? "piece" : "pieces"} for a marketplace</DialogTitle>
          <DialogDescription className="text-[#525252]">
            Download a ready-to-use <strong>.zip</strong>: a details sheet (opens in Excel or Google Sheets) <em>plus</em> every
            photo you've stored — one folder per piece. Your data and your images, together, so there's nothing to hunt for.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-1">
          <div>
            <label className="label-caps block mb-1.5">Marketplace</label>
            <select data-testid="export-platform-select" value={platform} onChange={(e) => setPlatform(e.target.value)} className="h-12 w-full border border-[#0D1B2A]/30 rounded-none px-2 text-[15px] focus:border-[#0D1B2A] outline-none">
              {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <p className="text-xs text-[#8A867D] mt-1.5">Uses each piece's price for this marketplace (falls back to your base price). Photos are pulled straight from your stored listings.</p>
          </div>
          <button data-testid="export-download-button" onClick={download} disabled={busy || !count} className="w-full h-14 bg-[#0D1B2A] text-[#FDFBF7] rounded-none font-medium text-[17px] inline-flex items-center justify-center gap-2 hover:bg-black transition-transform active:scale-[0.99] disabled:opacity-60">
            {busy ? "Building your pack…" : "Download pack (.zip)"} <Archive size={18} weight="fill" />
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Portfolio() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [listings, setListings] = useState([]);
  const [wins, setWins] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState("");
  const [soldTarget, setSoldTarget] = useState(null);
  const [rerouteTarget, setRerouteTarget] = useState(null);
  const [shareStory, setShareStory] = useState(null);
  const [contribute, setContribute] = useState(false);
  const [filter, setFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  const load = async () => {
    try {
      const [p, l, w, c] = await Promise.all([
        api.get("/portfolio"), api.get("/listings"), api.get("/portfolio/wins"), api.get("/me/contribute-stats"),
      ]);
      setStats(p.data);
      setListings(l.data || []);
      setWins(w.data);
      setContribute(!!c.data.contribute_stats);
    } catch (e) {
      toast.error("Couldn't load your portfolio");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const download = async (format) => {
    setExporting(format);
    try {
      const res = await api.get(`/portfolio/export?format=${format}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vintage_vantage_portfolio.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Your portfolio ${format.toUpperCase()} is downloading — it's yours to keep.`);
    } catch (e) {
      toast.error("Export failed — please try again");
    } finally {
      setExporting("");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const toggleContribute = async () => {
    try {
      const { data } = await api.patch("/me/contribute-stats", { contribute_stats: !contribute });
      setContribute(!!data.contribute_stats);
      toast.success(data.contribute_stats ? "Thanks — your anonymized stats now help the community." : "Opted out. Your data stays private to you.");
    } catch (e) {
      toast.error("Couldn't update your preference");
    }
  };

  const onSold = (story) => {
    setSoldTarget(null);
    setShareStory(story);
    toast.success("Nice sale! Logged to your portfolio. 🎉");
    load();
  };

  const shareWin = async (l) => {
    try {
      const { data: blob } = await api.get(`/listings/${l.id}/win-card.png`, { responseType: "blob" });
      const file = new File([blob], "vintage-vantage-win.png", { type: "image/png" });
      const caption = `Sold my ${l.listing_headline || "vintage piece"} — scored & sold with Vintage Vantage. #vintage #flip`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: "My Vintage Vantage win", text: caption });
          return;
        } catch (e) {
          if (e?.name === "AbortError") return;
        }
      }
      try { await navigator.clipboard.writeText(caption); } catch (e) { /* ignore */ }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "vintage-vantage-win.png";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Win card saved + caption copied — post it anywhere! 📲");
    } catch (e) {
      toast.error(e?.response?.status === 404 ? "Add a photo to this piece first." : "Couldn't build the share image.");
    }
  };

  const onRerouted = (updated, platform) => {
    setRerouteTarget(null);
    toast.success(`On to ${platform} — their loss! 💪`);
    load();
  };

  const onPlatformSaved = (updated) => {
    setListings((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
  };

  const toggleSelect = (id) =>
    setSelectedIds((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  const onBulkApplied = () => {
    setBulkOpen(false);
    setSelectedIds(new Set());
    load();
  };

  const sellerName = user?.name || user?.email || "A Vintage Vantage seller";
  const sc = stats?.status_counts || {};
  const drafts = listings.filter((l) => l.status === "Draft");
  const soldCount = listings.filter((l) => l.status === "Sold").length;
  const platformCounts = (() => {
    const map = {};
    for (const l of listings) for (const p of platformsForListing(l)) map[p] = (map[p] || 0) + 1;
    const order = { Chairish: 0 };
    return Object.entries(map)
      .map(([platform, count]) => ({ platform, count }))
      .sort((a, b) => (order[a.platform] ?? 9) - (order[b.platform] ?? 9) || b.count - a.count);
  })();
  const filtered = listings.filter((l) => {
    if (filter === "all") return true;
    if (filter === "__sold") return l.status === "Sold";
    return platformsForListing(l).includes(filter);
  });
  const allFilteredSelected = filtered.length > 0 && filtered.every((l) => selectedIds.has(l.id));
  const toggleSelectAll = () =>
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (allFilteredSelected) filtered.forEach((l) => n.delete(l.id));
      else filtered.forEach((l) => n.add(l.id));
      return n;
    });
  const metrics = [
    { icon: Archive, label: "Pieces owned", value: stats?.total ?? "—", testid: "metric-total" },
    { icon: CurrencyDollar, label: "Catalog value", value: fmtPrice(stats?.total_catalog_value), testid: "metric-value" },
    { icon: ImageSquare, label: "Photos stored", value: stats?.photos ?? "—", testid: "metric-photos" },
    { icon: Clock, label: "Member since", value: fmtMonth(stats?.member_since), testid: "metric-member-since" },
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="portfolio-page">
      <AnnouncementBanner />
      <header className="sticky top-0 z-30 border-b border-[#D8D3C8] bg-[#FDFBF7]/90 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/vv_logo.jpg" alt="Vintage Vantage" onClick={() => navigate("/dashboard")} className="h-9 w-9 rounded-full object-cover ring-1 ring-[#E5E0D6] cursor-pointer shrink-0 hover:ring-[#B47A3C] transition-all" data-testid="app-compass-mark" title="Vintage Vantage — home" />
            <button onClick={() => navigate("/dashboard")} data-testid="portfolio-back-button" className="inline-flex items-center gap-2 font-medium hover:text-[#B47A3C] transition-colors">
              <ArrowLeft size={18} weight="bold" /> Dashboard
            </button>
          </div>
          <div className="flex items-center gap-3">
            {user?.is_admin && (
              <>
                <button onClick={() => navigate("/admin/founding")} data-testid="portfolio-admin-founding-link" title="Founding Council signups" className="hidden sm:inline-flex items-center gap-1.5 h-10 px-3 border border-[#B47A3C] text-[#B47A3C] rounded-none text-sm font-semibold hover:bg-[#B47A3C] hover:text-white transition-colors">
                  <Crown size={15} weight="fill" /> Founding signups
                </button>
                <button onClick={() => navigate("/admin/insights")} data-testid="portfolio-admin-insights-link" title="Placement signal" className="hidden sm:inline-flex items-center gap-1.5 h-10 px-3 border border-[#0D1B2A] text-[#0D1B2A] rounded-none text-sm font-semibold hover:bg-[#0D1B2A] hover:text-white transition-colors">
                  <ChartLineUp size={15} weight="fill" /> Insights
                </button>
              </>
            )}
            <span className="text-sm font-semibold text-[#525252] max-w-[160px] truncate" data-testid="portfolio-user">{user?.name || user?.email}</span>
            <button onClick={handleLogout} data-testid="portfolio-logout-button" title="Sign out" className="h-10 w-10 rounded-full border border-[#D8D3C8] flex items-center justify-center hover:bg-[#0D1B2A] hover:text-white transition-colors">
              <SignOut size={18} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex items-center gap-2 text-[#0D1B2A] mb-2">
          <Vault size={22} weight="fill" />
          <span className="label-caps">Owned by you · portable forever</span>
        </div>
        <h1 className="font-serif-display text-5xl sm:text-6xl tracking-tight leading-none font-medium mb-3">Your Stored Portfolio</h1>
        <p className="text-lg text-[#525252] leading-relaxed max-w-2xl mb-10">
          Every listing you enrich here is a reusable asset that compounds in value. Download the whole database anytime —
          no marketplace can hold your work hostage.
        </p>

        {/* Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="portfolio-metrics">
          {metrics.map((m) => (
            <div key={m.label} data-testid={m.testid} className="border border-[#0D1B2A]/20 bg-[#FDFBF7] p-6">
              <m.icon size={22} weight="fill" className="text-[#0D1B2A] mb-3" />
              <p className="font-serif-display text-3xl font-bold leading-none">{loading ? "…" : m.value}</p>
              <p className="label-caps mt-2">{m.label}</p>
            </div>
          ))}
        </div>

        {/* Your wins */}
        {wins && wins.count > 0 && (
          <div className="bg-[#0D1B2A] text-[#FDFBF7] p-6 sm:p-8 mb-8" data-testid="wins-panel">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <div>
                <div className="flex items-center gap-2 mb-1"><Trophy size={20} weight="fill" className="text-[#B47A3C]" /> <span className="label-caps" style={{ color: "#E5E5E5" }}>Your wins so far</span></div>
                <h2 className="font-serif-display text-2xl sm:text-3xl font-bold">
                  {wins.count} {wins.count === 1 ? "sale" : "sales"} logged{wins.total_profit != null ? ` · ${fmtPrice(wins.total_profit)} profit` : ""}
                </h2>
              </div>
              {wins.best_platform && (
                <div className="border border-white/15 px-4 py-2">
                  <p className="label-caps" style={{ color: "#B8B8B8" }}>Top platform</p>
                  <p className="font-serif-display text-xl font-bold" data-testid="wins-best-platform">{wins.best_platform}</p>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { icon: CurrencyDollar, label: "Total revenue", value: fmtPrice(wins.total_revenue), testid: "wins-revenue" },
                { icon: TrendUp, label: "Total profit", value: wins.total_profit != null ? fmtPrice(wins.total_profit) : "—", testid: "wins-profit" },
                { icon: Trophy, label: "Avg return", value: wins.avg_multiple != null ? `${wins.avg_multiple}×` : "—", testid: "wins-multiple" },
                { icon: Clock, label: "Avg days to sale", value: wins.avg_days_to_sale != null ? `${wins.avg_days_to_sale} days` : "—", testid: "wins-days" },
                ...(wins.sell_through_pct != null ? [{ icon: TrendUp, label: "Sell-through", value: `${wins.sell_through_pct}%`, testid: "wins-sellthrough" }] : []),
              ].map((s) => (
                <div key={s.label} data-testid={s.testid} className="border border-white/10 bg-white/5 p-4">
                  <s.icon size={18} weight="fill" className="text-[#B47A3C] mb-2" />
                  <p className="font-serif-display text-3xl font-bold leading-none">{s.value}</p>
                  <p className="label-caps mt-2" style={{ color: "#B8B8B8" }}>{s.label}</p>
                </div>
              ))}
            </div>
            {wins.platform_breakdown?.length > 0 && (
              <div className="mt-6" data-testid="wins-platform-breakdown">
                <p className="label-caps mb-3" style={{ color: "#B8B8B8" }}>By platform</p>
                <div className="space-y-2">
                  {wins.platform_breakdown.map((pb) => {
                    const max = Math.max(...wins.platform_breakdown.map((x) => x.revenue || 0), 1);
                    return (
                      <div key={pb.platform} className="flex items-center gap-3">
                        <span className="w-28 sm:w-32 shrink-0 text-sm font-semibold truncate">{pb.platform}</span>
                        <div className="flex-1 h-3 bg-white/10 overflow-hidden">
                          <div className="h-full bg-[#B47A3C]" style={{ width: `${Math.max(6, ((pb.revenue || 0) / max) * 100)}%` }} />
                        </div>
                        <span className="w-36 sm:w-44 shrink-0 text-right text-sm text-[#E5E5E5]">{fmtPrice(pb.revenue)} · {pb.count} {pb.count === 1 ? "sale" : "sales"}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Finish your drafts */}
        {drafts.length > 0 && (
          <div className="border-2 border-[#0D1B2A] bg-white p-6 mb-8" data-testid="drafts-panel">
            <div className="flex items-center gap-2 mb-1"><PencilSimple size={20} weight="fill" /> <span className="label-caps">Finish your drafts</span></div>
            <h2 className="font-serif-display text-2xl sm:text-3xl font-bold mb-1">
              {drafts.length} {drafts.length === 1 ? "piece is" : "pieces are"} waiting to be finished
            </h2>
            <p className="text-[15px] text-[#525252] mb-5">A few details away from being ready to list. Pick up where you left off.</p>
            <div className="space-y-2">
              {drafts.slice(0, 8).map((d, i) => {
                const need = NEED_LABELS[d.review_status] || NEED_LABELS.ready;
                return (
                  <div key={d.id} data-testid={`draft-row-${i}`} className="flex items-center justify-between gap-3 border border-[#F0EFEA] px-4 py-3 hover:bg-[#FDFBF7] transition-colors">
                    <div className="min-w-0">
                      <p className="font-medium text-[#0D1B2A] truncate">{d.listing_headline || "Untitled piece"}</p>
                      <span className="text-sm font-semibold" style={{ color: need.color }}>{need.text}</span>
                    </div>
                    <button onClick={() => navigate(`/review/${d.id}`)} data-testid={`finish-draft-${i}`} className="shrink-0 h-11 px-4 bg-[#0D1B2A] text-[#FDFBF7] rounded-none font-medium inline-flex items-center gap-2 hover:bg-black transition-transform active:scale-95">
                      Finish this <ArrowRight size={15} weight="bold" />
                    </button>
                  </div>
                );
              })}
            </div>
            {drafts.length > 8 && <p className="text-sm text-[#8A867D] mt-3">+ {drafts.length - 8} more below.</p>}
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-4 mb-10">
          {/* Status breakdown */}
          <div className="lg:col-span-2 border border-[#0D1B2A]/20 bg-white p-6" data-testid="portfolio-status-breakdown">
            <h2 className="font-serif-display text-2xl font-bold mb-4">Where your pieces stand</h2>
            <div className="grid grid-cols-3 gap-4">
              {[
                { k: "Draft", label: "In progress", color: "#8A867D" },
                { k: "Exported", label: "Listed / cross-listed", color: "#047857" },
                { k: "Sold", label: "Sold", color: "#B47A3C" },
              ].map((s) => (
                <div key={s.k} data-testid={`status-count-${s.k.toLowerCase()}`}>
                  <p className="font-serif-display text-4xl font-bold leading-none" style={{ color: s.color }}>{loading ? "…" : sc[s.k] || 0}</p>
                  <p className="text-sm text-[#525252] mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Own your database export */}
          <div className="bg-[#0D1B2A] text-[#FDFBF7] p-6 flex flex-col" data-testid="portfolio-export-card">
            <h2 className="font-serif-display text-2xl font-bold mb-1">Own your database</h2>
            <p className="text-[15px] text-[#E5E5E5] leading-relaxed mb-5">Download your entire portfolio. Take it anywhere.</p>
            <div className="mt-auto space-y-3">
              <button
                onClick={() => download("csv")}
                disabled={exporting === "csv" || !stats?.total}
                data-testid="export-csv-button"
                className="w-full h-14 bg-[#FDFBF7] text-[#0D1B2A] rounded-none font-medium inline-flex items-center justify-center gap-2 hover:bg-white transition-transform active:scale-[0.98] disabled:opacity-50"
              >
                <FileCsv size={20} weight="fill" /> {exporting === "csv" ? "Preparing…" : "Export CSV"}
              </button>
              <button
                onClick={() => download("json")}
                disabled={exporting === "json" || !stats?.total}
                data-testid="export-json-button"
                className="w-full h-14 border border-[#FDFBF7] rounded-none font-medium inline-flex items-center justify-center gap-2 hover:bg-white/10 transition-colors disabled:opacity-50"
              >
                <FileJs size={20} weight="fill" /> {exporting === "json" ? "Preparing…" : "Export JSON"}
              </button>
            </div>
          </div>
        </div>

        {/* Community contribution opt-in */}
        <div className="border border-[#0D1B2A]/20 bg-[#F5F3ED] p-5 mb-10 flex flex-wrap items-center justify-between gap-4" data-testid="contribute-toggle-card">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-1"><ChartLineUp size={18} weight="fill" /> <span className="label-caps">Community stats</span></div>
            <p className="text-[15px] text-[#525252] leading-relaxed">
              Help sharpen everyone's pricing — contribute your sale outcomes, <strong>anonymized and aggregated</strong>.
              Opt-in and revocable anytime; your individual listings are never shared.
            </p>
          </div>
          <button onClick={toggleContribute} data-testid="contribute-toggle" className={`shrink-0 h-12 px-5 rounded-none font-medium inline-flex items-center gap-2 border transition-colors ${contribute ? "bg-[#0D1B2A] text-white border-[#0D1B2A]" : "bg-white text-[#0D1B2A] border-[#0D1B2A] hover:bg-[#0D1B2A] hover:text-white"}`}>
            {contribute ? <><CheckCircle size={16} weight="fill" /> Contributing</> : "Contribute my stats"}
          </button>
        </div>

        {/* Bulk action bar */}
        {selectedIds.size > 0 && (
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3 bg-[#0D1B2A] text-white px-4 py-3" data-testid="bulk-action-bar">
            <span className="font-semibold">{selectedIds.size} selected</span>
            <div className="flex items-center gap-2">
              <button onClick={() => setBulkOpen(true)} data-testid="bulk-price-button" className="h-10 px-4 bg-white text-[#0D1B2A] rounded-none text-sm font-semibold inline-flex items-center gap-1.5 hover:bg-[#F5F3ED] transition-colors">
                <Tag size={15} weight="fill" /> Set marketplace price
              </button>
              <button onClick={() => setExportOpen(true)} data-testid="bulk-export-button" className="h-10 px-4 bg-white text-[#0D1B2A] rounded-none text-sm font-semibold inline-flex items-center gap-1.5 hover:bg-[#F5F3ED] transition-colors">
                <FileCsv size={15} weight="fill" /> Export pack
              </button>
              <button onClick={() => setSelectedIds(new Set())} data-testid="bulk-clear-button" className="h-10 px-3 border border-white/40 rounded-none text-sm font-medium hover:bg-white/10 transition-colors">Clear</button>
            </div>
          </div>
        )}

        {/* Data table */}
        <div className="border border-[#0D1B2A]/20 bg-white overflow-hidden" data-testid="portfolio-table">
          <div className="px-6 py-4 border-b border-[#F0EFEA] flex items-center justify-between">
            <h2 className="font-serif-display text-2xl font-bold">Your pieces</h2>
            <span className="text-sm text-[#8A867D]">{filter === "all" ? `${listings.length} total` : `${filtered.length} of ${listings.length}`}</span>
          </div>
          {listings.length > 0 && (
            <div className="px-6 py-3 border-b border-[#F0EFEA] bg-[#FDFBF7] flex flex-wrap items-center gap-2" data-testid="portfolio-filter-chips">
              <span className="label-caps mr-1">Filter by marketplace</span>
              <FilterChip label="All" count={listings.length} active={filter === "all"} onClick={() => setFilter("all")} testid="filter-all" />
              {platformCounts.map((pc) => (
                <FilterChip key={pc.platform} label={pc.platform} count={pc.count} active={filter === pc.platform} onClick={() => setFilter(pc.platform)} testid={`filter-${pc.platform.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
              ))}
              {soldCount > 0 && <FilterChip label="Sold" count={soldCount} active={filter === "__sold"} onClick={() => setFilter("__sold")} testid="filter-sold" />}
            </div>
          )}
          {loading ? (
            <div className="p-10 text-center text-[#8A867D]">Loading your pieces…</div>
          ) : listings.length === 0 ? (
            <div className="p-10 text-center">
              <p className="text-[#525252] mb-4">Your portfolio is empty. Import or capture your first piece to start building.</p>
              <button onClick={() => navigate("/dashboard")} data-testid="portfolio-empty-cta" className="h-12 px-6 bg-[#0D1B2A] text-[#FDFBF7] rounded-none font-medium">Go to dashboard</button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[15px]">
                <thead>
                  <tr className="text-left border-b border-[#F0EFEA] bg-[#F5F3ED]">
                    <th className="px-4 py-3"><input type="checkbox" data-testid="select-all" checked={allFilteredSelected} onChange={toggleSelectAll} className="h-4 w-4 accent-[#0D1B2A] align-middle" /></th>
                    <th className="px-6 py-3 label-caps font-semibold">Piece</th>
                    <th className="px-4 py-3 label-caps font-semibold">Brand / Designer</th>
                    <th className="px-4 py-3 label-caps font-semibold">Price</th>
                    <th className="px-4 py-3 label-caps font-semibold">Where it's for sale</th>
                    <th className="px-4 py-3 label-caps font-semibold">Status</th>
                    <th className="px-4 py-3 label-caps font-semibold text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((l, i) => (
                    <Fragment key={l.id}>
                    <tr data-testid={`portfolio-row-${i}`} className={`border-b border-[#F7F6F1] ${i % 2 ? "bg-[#FDFBF7]" : "bg-white"}`}>
                      <td className="px-4 py-3"><input type="checkbox" data-testid={`select-${i}`} checked={selectedIds.has(l.id)} onChange={() => toggleSelect(l.id)} className="h-4 w-4 accent-[#0D1B2A] align-middle" /></td>
                      <td className="px-6 py-3 font-medium text-[#0D1B2A] max-w-sm">
                        <button onClick={() => setExpandedId(expandedId === l.id ? null : l.id)} data-testid={`expand-${i}`} className="inline-flex items-start gap-2 text-left w-full hover:text-[#B47A3C] transition-colors">
                          <CaretRight size={14} weight="bold" className={`mt-1 shrink-0 transition-transform ${expandedId === l.id ? "rotate-90" : ""}`} />
                          <span className="block truncate">{l.listing_headline || "Untitled piece"}</span>
                        </button>
                      </td>
                      <td className="px-4 py-3 text-[#525252]">{l.brand_designer || "—"}</td>
                      <td className="px-4 py-3 font-semibold">{fmtPrice(l.price_retail)}</td>
                      <td className="px-4 py-3" data-testid={`marketplace-cell-${i}`}>
                        <div className="flex flex-wrap gap-1">
                          {l.status === "Sold" ? (
                            <span className="text-xs px-2 py-0.5 rounded-full border border-[#F5C9B8] text-[#B47A3C] bg-[#FBEDE7]">Sold{l.sold_platform ? ` · ${l.sold_platform}` : ""}</span>
                          ) : (
                            <>
                              {platformEntries(l).length > 0 && (
                                <PlatformBadgeRow entries={platformEntries(l)} size={22} />
                              )}
                              {l.next_platform && (
                                <span data-testid={`next-platform-${i}`} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border border-[#BFDBFE] text-[#1E40AF] bg-[#EFF6FF]">
                                  <PaperPlaneTilt size={10} weight="fill" /> {l.next_platform}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3"><StatusBadge status={l.review_status} /></td>
                      <td className="px-4 py-3 text-right">
                        {l.status === "Sold" ? (
                          <div className="inline-flex items-center gap-2 justify-end">
                            <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#B47A3C]"><CheckCircle size={16} weight="fill" /> Sold</span>
                            <button onClick={() => shareWin(l)} data-testid={`share-win-${i}`} title="Download a branded share card of this win" className="h-10 px-3 border border-[#0D1B2A] rounded-none text-sm font-medium inline-flex items-center gap-1.5 hover:bg-[#0D1B2A] hover:text-white transition-colors">
                              <ShareNetwork size={15} weight="fill" /> Share win
                            </button>
                          </div>
                        ) : (
                          <div className="inline-flex items-center gap-2 justify-end">
                            <button onClick={() => setRerouteTarget(l)} data-testid={`reroute-${i}`} title="Turned down? Send it to its next best home." className="h-10 px-3 border border-[#D8D3C8] rounded-none text-sm font-medium inline-flex items-center gap-1.5 text-[#525252] hover:border-[#0D1B2A] hover:text-[#0D1B2A] transition-colors">
                              <PaperPlaneTilt size={15} weight="fill" /> Rejected?
                            </button>
                            <button onClick={() => setSoldTarget(l)} data-testid={`mark-sold-${i}`} className="h-10 px-3 border border-[#0D1B2A] rounded-none text-sm font-medium inline-flex items-center gap-1.5 hover:bg-[#0D1B2A] hover:text-white transition-colors">
                              <Storefront size={15} weight="fill" /> Mark sold
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                    {expandedId === l.id && (
                      <tr data-testid={`platform-editor-row-${i}`} className="bg-[#FDFBF7]">
                        <td colSpan={7} className="px-6 pb-5 pt-1">
                          <PlatformPricingEditor listing={l} onSaved={onPlatformSaved} />
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
              {filtered.length === 0 && (
                <div className="p-8 text-center text-[#8A867D]" data-testid="filter-empty">No pieces match this marketplace yet.</div>
              )}
            </div>
          )}
        </div>
      </main>

      <MarkSoldDialog listing={soldTarget} sellerName={sellerName} onClose={() => setSoldTarget(null)} onSold={onSold} />
      <RerouteDialog listing={rerouteTarget} onClose={() => setRerouteTarget(null)} onRerouted={onRerouted} />
      <BulkPriceDialog open={bulkOpen} items={listings.filter((l) => selectedIds.has(l.id))} onClose={() => setBulkOpen(false)} onApplied={onBulkApplied} />
      <ExportPackDialog open={exportOpen} count={selectedIds.size} ids={[...selectedIds]} onClose={() => setExportOpen(false)} />
      <ShareFlipDialog story={shareStory} onClose={() => setShareStory(null)} />
    </div>
  );
}
